import { HStack, Slider } from "@chakra-ui/react";
import { CrashType, RiskFactor, riskFactorColors } from "@keita/lanepatrol-sdk";
import { ScoreBox } from "./ScoreBox";
import {
  ScoreFilter,
  ScoreFilterType,
  ResultsScoreLimits,
} from "../../../../../map/models/MapFilters";
import { calculateRiskFactor } from "../../../../../coding/utils/score-utils";

const colors = Object.values(RiskFactor).map((rf) => riskFactorColors[rf]);
const crashTypeBreakpoints = {
  [CrashType.vehicleToBicycle]: [10, 25, 60],
  [CrashType.bicycleToBicycle]: [5, 10, 20],
  [CrashType.bicycleToPedestrian]: [5, 10, 20],
  [CrashType.singleBicycle]: [5, 10, 20],
};

const buildGradient = (breakpoints: number[], limit: ResultsScoreLimits) => {
  let currValue = 0;
  return colors.reduce<string[]>((acc, curr, i) => {
    acc.push(`${curr} ${toPercent(currValue, limit)}%`);
    currValue = breakpoints[i] ?? limit.max;
    acc.push(`${curr} ${toPercent(currValue, limit)}%`);
    return acc;
  }, []);
};

const toPercent = (x: number, limit: ResultsScoreLimits) =>
  ((x - limit.min) / (limit.max - limit.min)) * 100;
const fromPercent = (x: number, limit: ResultsScoreLimits) =>
  (x / 100) * (limit.max - limit.min) + limit.min;

interface Props {
  filter?: ScoreFilter;
  crashType: CrashType;
  setFilter: (filter: ScoreFilter) => void;
  limit: ResultsScoreLimits;
}
export const ScoreFilterItem: React.FC<Props> = ({
  filter,
  crashType,
  setFilter,
  limit,
}) => {
  const gradient = buildGradient(crashTypeBreakpoints[crashType], limit);
  const value =
    filter?.type === ScoreFilterType.Limits
      ? [filter.limits.min ?? limit.min, filter.limits.max ?? limit.max]
      : [limit.min, limit.max];
  const valuePercent = [toPercent(value[0], limit), toPercent(value[1], limit)];

  const minColor = riskFactorColors[calculateRiskFactor(value[0], crashType)];
  const maxColor = riskFactorColors[calculateRiskFactor(value[1], crashType)];

  return (
    <HStack my={2} alignItems="center">
      <ScoreBox showValue color={minColor} value={Math.round(value[0])} />
      <Slider.Root
        flexGrow={1}
        value={valuePercent}
        onValueChange={({ value }) => {
          const [min, max] = value;
          setFilter({
            type: ScoreFilterType.Limits,
            limits: {
              min: fromPercent(min, limit),
              max: fromPercent(max, limit),
            },
          });
        }}
      >
        <Slider.Control>
          <Slider.Track bg={`linear-gradient(90deg, ${gradient})`}>
            <Slider.Range bg="transparent" />
          </Slider.Track>
          <Slider.Thumb bg={minColor} index={0} />
          <Slider.Thumb bg={maxColor} index={1} />
        </Slider.Control>
      </Slider.Root>
      <ScoreBox showValue color={maxColor} value={Math.round(value[1])} />
    </HStack>
  );
};
